import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, User, Mail, MessageSquare, CheckCircle, Search, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { getApiBaseUrl } from '../utils/api';
import { debugLog, debugWarn, debugInfo, debugError } from '../utils/debug';
import './ContactModal.css';

const ContactModal = ({ isOpen, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState('');
  
  // Ticket viewer states
  const [showTicketViewer, setShowTicketViewer] = useState(false);
  const [loadTicketId, setLoadTicketId] = useState('');
  const [currentTicket, setCurrentTicket] = useState(null);
  const [isLoadingTicket, setIsLoadingTicket] = useState(false);
  const [newReply, setNewReply] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !subject || !message) {
      toast.error('Please fill in all fields');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      debugLog('📬 Submitting contact form...');
      
      const response = await fetch(`${getApiBaseUrl()}/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject: subject.trim(),
          message: message.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create ticket');
      }

      debugLog('✅ Ticket created successfully:', data.ticketId);
      
      setTicketId(data.ticketId);
      setIsSubmitted(true);
      toast.success('Ticket created successfully! We\'ll get back to you soon.');
      
      // Clear form fields
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
      
      // Don't auto close, let user see the ticket ID
      
    } catch (error) {
      debugError('❌ Error submitting contact form:', error);
      toast.error(error.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return; // Prevent closing while submitting
    
    setName('');
    setEmail('');
    setSubject('');
    setMessage('');
    setIsSubmitted(false);
    setTicketId('');
    setShowTicketViewer(false);
    setLoadTicketId('');
    setCurrentTicket(null);
    setNewReply('');
    onClose();
  };

  const loadTicket = async () => {
    if (!loadTicketId.trim()) {
      toast.error('Please enter a ticket ID');
      return;
    }

    setIsLoadingTicket(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/tickets/${loadTicketId.trim()}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load ticket');
      }

      setCurrentTicket(data.ticket);
      setShowTicketViewer(true);
      toast.success('Ticket loaded successfully!');
    } catch (error) {
      debugError('Error loading ticket:', error);
      toast.error(error.message || 'Failed to load ticket');
    } finally {
      setIsLoadingTicket(false);
    }
  };

  const sendReply = async () => {
    if (!newReply.trim()) {
      toast.error('Please enter a reply message');
      return;
    }

    if (!currentTicket) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/tickets/${currentTicket.ticketId}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: newReply.trim(),
          authorName: name || currentTicket.name
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send reply');
      }

      // Clear the reply field first
      setNewReply('');
      
      // Refresh ticket to show the new reply from database
      await refreshTicket();
      
      toast.success('Reply sent successfully!');
    } catch (error) {
      debugError('Error sending reply:', error);
      toast.error(error.message || 'Failed to send reply');
    } finally {
      setIsSubmitting(false);
    }
  };

  const refreshTicket = async () => {
    if (!currentTicket) return;

    setIsLoadingTicket(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/tickets/${currentTicket.ticketId}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to refresh ticket');
      }

      setCurrentTicket(data.ticket);
      toast.success('Ticket refreshed!');
    } catch (error) {
      debugError('Error refreshing ticket:', error);
      toast.error(error.message || 'Failed to refresh ticket');
    } finally {
      setIsLoadingTicket(false);
    }
  };

  // Handle Escape key
  React.useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen && !isSubmitting) {
        handleClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, isSubmitting]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="contact-modal-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          className="contact-modal"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="contact-modal-header">
            <div className="contact-header-content">
              <MessageSquare size={24} className="contact-icon" />
              <h2>Contact Us</h2>
            </div>
            <button 
              className="contact-modal-close"
              onClick={handleClose}
              disabled={isSubmitting}
              aria-label="Close Contact Modal"
            >
              <X size={24} />
            </button>
          </div>

          <div className="contact-modal-content">
            {showTicketViewer && currentTicket ? (
              <div className="ticket-viewer">
                <div className="ticket-header">
                  <div className="ticket-title">
                    <h3>Ticket #{currentTicket.ticketId}</h3>
                    <button
                      onClick={refreshTicket}
                      disabled={isLoadingTicket}
                      className="refresh-ticket-btn"
                      title="Refresh ticket to load new messages"
                    >
                      <RefreshCw size={16} className={isLoadingTicket ? 'spin' : ''} />
                    </button>
                  </div>
                  <div className="ticket-status">
                    <span className={`status-badge ${currentTicket.status}`}>
                      {currentTicket.status}
                    </span>
                  </div>
                </div>
                
                <div className="ticket-details">
                  <div className="ticket-meta">
                    <p><strong>Subject:</strong> {currentTicket.subject}</p>
                    <p><strong>From:</strong> {currentTicket.name} ({currentTicket.email})</p>
                    <p><strong>Created:</strong> {new Date(currentTicket.createdAt).toLocaleString()}</p>
                  </div>
                  
                  <div className="original-message">
                    <h4>Original Message:</h4>
                    <div className="message-content">{currentTicket.message}</div>
                  </div>
                  
                  {currentTicket.responses && currentTicket.responses.length > 0 && (
                    <div className="responses">
                      <h4>Conversation:</h4>
                      <div className="responses-list">
                        {currentTicket.responses.map((response, index) => (
                          <div key={index} className={`response ${response.author}`}>
                            <div className="response-meta">
                              <span className="author">{response.authorName}</span>
                              <span className="time">{new Date(response.timestamp).toLocaleString()}</span>
                            </div>
                            <div className="response-content">{response.content}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="reply-section">
                    <h4>Add Reply:</h4>
                    <textarea
                      value={newReply}
                      onChange={(e) => setNewReply(e.target.value)}
                      placeholder="Type your reply here..."
                      rows={4}
                      className="reply-textarea"
                      disabled={isSubmitting}
                    />
                    <div className="reply-actions">
                      <button
                        onClick={() => setShowTicketViewer(false)}
                        className="btn btn-secondary"
                        disabled={isSubmitting}
                      >
                        Back to Form
                      </button>
                      <button
                        onClick={sendReply}
                        className="btn btn-primary"
                        disabled={isSubmitting || !newReply.trim()}
                      >
                        {isSubmitting ? 'Sending...' : 'Send Reply'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : !isSubmitted ? (
              <>
                <div className="contact-info">
                  <p>
                    We'd love to hear from you! Whether you have questions, feedback, or need support, 
                    our team is here to help. Send us a message and we'll get back to you as soon as possible.
                  </p>
                  <div className="contact-features">
                    <div className="feature-item">
                      <span className="feature-icon">⚡</span>
                      <span>Quick response times</span>
                    </div>
                    <div className="feature-item">
                      <span className="feature-icon">🔒</span>
                      <span>Your privacy is protected</span>
                    </div>
                    <div className="feature-item">
                      <span className="feature-icon">💬</span>
                      <span>Direct support team access</span>
                    </div>
                  </div>
                </div>
                
                <div className="load-ticket-section">
                  <button
                    onClick={() => setShowTicketViewer(!showTicketViewer)}
                    className="load-ticket-toggle"
                    type="button"
                  >
                    <Search size={16} />
                    Load Existing Ticket
                    <span className={`chevron ${showTicketViewer ? 'open' : ''}`}>▼</span>
                  </button>
                  
                  <AnimatePresence>
                    {showTicketViewer && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="load-ticket-form"
                      >
                        <div className="load-ticket-input">
                          <input
                            type="text"
                            value={loadTicketId}
                            onChange={(e) => setLoadTicketId(e.target.value)}
                            placeholder="Enter ticket ID (e.g., TCK-A7F9)"
                            className="ticket-id-input"
                            disabled={isLoadingTicket}
                          />
                          <button
                            onClick={loadTicket}
                            disabled={isLoadingTicket || !loadTicketId.trim()}
                            className="btn btn-primary"
                          >
                            {isLoadingTicket ? <RefreshCw size={16} className="spin" /> : <Search size={16} />}
                            Load
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">
                        <User size={16} />
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter your full name"
                        required
                        disabled={isSubmitting}
                        className="contact-input"
                        maxLength={100}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">
                        <Mail size={16} />
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your.email@example.com"
                        required
                        disabled={isSubmitting}
                        className="contact-input"
                        maxLength={150}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="subject">
                      <MessageSquare size={16} />
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="What's this about?"
                      required
                      disabled={isSubmitting}
                      className="contact-input"
                      maxLength={200}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">
                      <Send size={16} />
                      Message *
                    </label>
                    <textarea
                      id="message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Tell us more about your inquiry, feedback, or how we can help you..."
                      rows={5}
                      required
                      disabled={isSubmitting}
                      className="contact-textarea"
                      maxLength={2000}
                    />
                    <div className="character-count">
                      {message.length}/2000
                    </div>
                  </div>

                  <div className="contact-actions">
                    <motion.button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleClose}
                      disabled={isSubmitting}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting || !name || !email || !subject || !message}
                      whileHover={!isSubmitting && name && email && subject && message ? { scale: 1.02 } : {}}
                      whileTap={!isSubmitting && name && email && subject && message ? { scale: 0.98 } : {}}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="spinner" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={16} />
                          Send Message
                        </>
                      )}
                    </motion.button>
                  </div>
                </form>
              </>
            ) : (
              <div className="contact-success">
                <div className="success-icon">
                  <CheckCircle size={48} />
                </div>
                <h3>Ticket Created!</h3>
                <div className="ticket-id-display">
                  <p>Your ticket ID is:</p>
                  <div className="ticket-id-code">#{ticketId}</div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(ticketId);
                      toast.success('Ticket ID copied to clipboard!');
                    }}
                    className="copy-ticket-id"
                  >
                    Copy ID
                  </button>
                </div>
                <p>Save this ticket ID to track your request. Our team will respond within 24 hours and all updates will be synced automatically.</p>
                <div className="success-actions">
                  <button
                    onClick={() => {
                      setLoadTicketId(ticketId);
                      setIsSubmitted(false);
                      setShowTicketViewer(true);
                      loadTicket();
                    }}
                    className="btn btn-primary"
                  >
                    View Ticket
                  </button>
                  <button
                    onClick={handleClose}
                    className="btn btn-secondary"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ContactModal;